import { forwardRef, type InputHTMLAttributes } from 'react';
import './Input.css';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | null;
  hint?: string;
}

/**
 * forwardRef: react-hook-form's register() spreads a ref — without it, the ref is
 * dropped with a React warning and focus/blur-based validation never reaches RHF.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, id, className, ...rest },
  ref,
) {
  const inputId = id ?? (label ? `field-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);
  return (
    <div className={`field${className ? ` ${className}` : ''}`}>
      {label && (
        <label className="field-label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        className={`field-input${error ? ' field-input-error' : ''}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && inputId ? `${inputId}-error` : undefined}
        {...rest}
      />
      {hint && !error && <p className="field-hint">{hint}</p>}
      {error && (
        <p className="field-error" id={inputId ? `${inputId}-error` : undefined} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
